import { ArrowRight, BarChart3, Target, TrendingUp, Zap } from "lucide-react";
// import Navbar from '@/components/Navbar';
import advancedAnalyticsImg from "../assets/growth-focused.jpg";
import preciseTargetingImg from "../assets/precise-targeting.jpg";
import growthFocusedImg from "../assets/growth-focused.jpg";
import realTimeResultsImg from "../assets/real-time-results.jpg";
import { Button } from "antd";
import Navbar2 from "../components/Navbar2";

const Index = () => {
  const features = [
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description:
        "Deep insights into your marketing performance with cutting-edge statistical models. Our advanced analytics platform provides comprehensive data visualization, predictive modeling, and actionable insights that help you understand your marketing performance at a granular level.",
      image: advancedAnalyticsImg,
      features: [
        "Real-time data processing",
        "Predictive modeling",
        "Custom dashboards",
        "Performance insights",
      ],
    },
    {
      icon: Target,
      title: "Precise Targeting",
      description:
        "AI-powered optimization that finds your highest-value customers automatically. Leverage machine learning algorithms to identify and target your most valuable customer segments with unprecedented precision and efficiency.",
      image: preciseTargetingImg,
      features: [
        "AI-powered segmentation",
        "Behavioral targeting",
        "Lookalike audiences",
        "Dynamic optimization",
      ],
    },
    {
      icon: TrendingUp,
      title: "Growth Focused",
      description:
        "Tools designed to scale your marketing efforts and maximize ROI. Our growth-focused approach ensures that every marketing dollar is optimized for maximum impact and sustainable business growth.",
      image: growthFocusedImg,
      features: [
        "ROI optimization",
        "Scalable campaigns",
        "Growth tracking",
        "Performance metrics",
      ],
    },
    {
      icon: Zap,
      title: "Real-time Results",
      description:
        "Get instant feedback and optimize campaigns as they run. Monitor your marketing performance in real-time and make data-driven adjustments to maximize your campaign effectiveness.",
      image: realTimeResultsImg,
      features: [
        "Live monitoring",
        "Instant alerts",
        "Quick adjustments",
        "Performance tracking",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar2 />
      <section className="pt-24 pb-20 gradient-hero relative overflow-hidden min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 animate-slide-up glow-text">
              Data-Driven
              <span className="block text-shimmer bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
                Marketing Excellence
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto mb-12 animate-fade-in leading-relaxed">
              Transform your marketing strategy with our advanced analytics
              platform. Measure true impact, optimize performance, and drive
              unprecedented growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in">
              <Button className="bg-white text-primary hover:bg-white/90 hover-glow shadow-large text-lg px-8 py-4 transition-spring">
                Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button className="border-white/30 text-white hover:bg-white/10 hover:border-white text-lg px-8 py-4 backdrop-blur-sm">
                Watch Demo
              </Button>
            </div>
          </div>
        </div>

        {/* Floating elements for visual appeal */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/20 rounded-full animate-float blur-sm"></div>
        <div
          className="absolute top-40 right-20 w-16 h-16 bg-pink-500/20 rounded-full animate-bounce-subtle blur-sm"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute bottom-20 left-1/4 w-12 h-12 bg-purple-400/30 rounded-full animate-float blur-sm"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-1/2 right-10 w-8 h-8 bg-pink-400/20 rounded-full animate-pulse-glow"
          style={{ animationDelay: "3s" }}
        ></div>
        <div
          className="absolute bottom-40 right-1/3 w-14 h-14 bg-purple-300/20 rounded-full animate-bounce-subtle blur-sm"
          style={{ animationDelay: "4s" }}
        ></div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Why Choose DataFlow?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our platform combines advanced analytics with user-friendly
              interfaces to deliver insights that drive real business results.
            </p>
          </div>

          <div className="space-y-20">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`flex flex-col ${
                    isEven ? "lg:flex-row" : "lg:flex-row-reverse"
                  } items-center gap-12 lg:gap-16 animate-fade-in`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Image Section */}
                  <div className="flex-1 relative group">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-card/50 to-card border border-border/50 backdrop-blur-sm shadow-glow hover:shadow-neon transition-all duration-500 hover:scale-105">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full h-80 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>

                      {/* Floating icon */}
                      <div className="absolute top-6 left-6 w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center shadow-glow group-hover:shadow-neon transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1 space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-3xl lg:text-4xl font-bold text-foreground glow-text">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>

                    {/* Feature list */}
                    <div className="space-y-3">
                      {feature.features.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="flex items-center gap-3"
                        >
                          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-purple-500 shadow-glow"></div>
                          <span className="text-foreground/80">{item}</span>
                        </div>
                      ))}
                    </div>

                    <Button className="gradient-primary text-white hover-glow shadow-glow transition-spring hover:scale-105">
                      Learn More
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-muted/30 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5"></div>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 glow-text">
            Ready to revolutionize your marketing?
          </h2>
          <p className="text-xl text-muted-foreground mb-10 leading-relaxed">
            Join thousands of marketers who trust DataFlow to drive their
            success.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button className="gradient-primary text-white hover-glow shadow-glow text-lg px-8 py-4 transition-spring">
              Get Started Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button className="hover-lift text-lg px-8 py-4 border-primary/30 hover:border-primary transition-spring">
              Talk to Sales
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
